export interface StaticRouteMetadata {
  path: string;
  title: string;
  description: string;
  noIndex?: boolean;
  /**
   * The URL search engines should treat as the real one, when this path is an
   * alias for another. Omitted means the path is its own canonical.
   */
  canonicalPath?: string;
}

export const PUBLIC_STATIC_ROUTES: StaticRouteMetadata[] = [
  { path: '/', title: 'Package Protection made simple', description: 'Enshield brings Package Protection, claims handling, and customer support together in one seamless experience.' },
  { path: '/platform', title: 'How Enshield works', description: 'From checkout to claim resolution, Enshield handles Package Protection for merchants and customers.', canonicalPath: '/how-it-works' },
  { path: '/how-it-works', title: 'How Enshield works', description: 'From checkout to claim resolution, Enshield handles Package Protection for merchants and customers.' },
  { path: '/for-customers', title: 'Enshield for customers', description: 'File and track an Enshield Protection claim using the merchant link and your private tracking token.' },
  { path: '/for-merchants', title: 'Enshield for merchants', description: 'Offer managed Package Protection at checkout while Enshield handles customer claims and resolutions.' },
  /*
   * `/customers` and `/merchants` are alias routes in App.tsx rendering the
   * same components as the `/for-*` paths. They were absent here, so the
   * prerender skipped them and nginx served the untouched template -- meaning
   * both URLs advertised the HOME PAGE's title and a canonical of
   * `https://enshield.com/`.
   *
   * Google executes JS and would eventually see the client-side correction, but
   * social scrapers do not: every share of an /customers or /merchants link
   * previewed as the homepage. Aliases are declared rather than removed because
   * the routes are already public and may be linked from outside.
   */
  { path: '/customers', title: 'Enshield for customers', description: 'File and track an Enshield Protection claim using the merchant link and your private tracking token.', canonicalPath: '/for-customers' },
  { path: '/merchants', title: 'Enshield for merchants', description: 'Offer managed Package Protection at checkout while Enshield handles customer claims and resolutions.', canonicalPath: '/for-merchants' },
  { path: '/integrations', title: 'How Enshield works', description: 'From checkout to claim resolution, Enshield handles Package Protection for merchants and customers.', canonicalPath: '/how-it-works' },
  { path: '/claims-center', title: 'Claims Center', description: 'File a new Enshield Package Protection claim or track an existing claim.' },
  { path: '/book-a-call', title: 'Book a Call', description: 'Choose an available time for a short introductory call with Enshield.' },
  { path: '/file-a-claim', title: 'File a claim', description: 'Open a merchant-specific Enshield claim form and submit a claim for a verified order.' },
  { path: '/track-claim', title: 'Track your claim', description: 'Use a private Enshield tracking token to view claim status, messages, evidence, and support.' },
  { path: '/faq', title: 'Frequently Asked Questions', description: 'Answers for merchants about Enshield Package Protection, claims, setup, and customer support.' },
  { path: '/contact', title: 'Contact Enshield', description: 'Contact Enshield about claim links, product support, or merchant integrations.' },
  { path: '/end-user-policy', title: 'End User Policy', description: 'The published Enshield Risk Solutions Package Protection Policy for protected shipments.' },
  { path: '/privacy-policy', title: 'Privacy Policy', description: 'The published Enshield Inc. Privacy Policy describing information collection, use, disclosure, retention, security, and privacy rights.' },
  { path: '/terms-of-service', title: 'Terms of Service', description: 'The published Enshield Platform Terms of Service governing access to and use of Enshield services.' },
  { path: '/learn-more', title: 'Learn More', description: 'Learn more about Enshield Package Protection, eligible shipping issues, claims, limitations, and availability.' },
  { path: '/privacy', title: 'Privacy Policy', description: 'The published Enshield Inc. Privacy Policy.', canonicalPath: '/privacy-policy' },
  { path: '/terms', title: 'Terms of Service', description: 'The published Enshield Platform Terms of Service.', canonicalPath: '/terms-of-service' },
  { path: '/404', title: 'Page not found', description: 'The requested Enshield page could not be found.', noIndex: true },
];

const BASE = 'https://enshield.com';

export function metadataForPath(path: string): StaticRouteMetadata | undefined {
  if (path.startsWith('/file-a-claim/')) return PUBLIC_STATIC_ROUTES.find((route) => route.path === '/file-a-claim');
  return PUBLIC_STATIC_ROUTES.find((route) => route.path === path);
}

function replaceMeta(html: string, selector: string, value: string): string {
  return html.replace(new RegExp(`(<meta ${selector} content=")[^"]*(")`), `$1${value}$2`);
}

export function renderStaticRouteHtml(template: string, path: string): string {
  const metadata = metadataForPath(path);
  if (!metadata) return template;
  const title = `${metadata.title} | Enshield`;
  // An alias points at the URL it duplicates, which is the whole purpose of a
  // canonical link; every other route is its own.
  const canonicalTarget = metadata.canonicalPath ?? path;
  const canonical = `${BASE}${canonicalTarget === '/' ? '/' : canonicalTarget}`;
  let html = template.replace(/<title>[^<]*<\/title>/, `<title>${title}</title>`);
  html = replaceMeta(html, 'name="description"', metadata.description);
  html = replaceMeta(html, 'name="robots"', metadata.noIndex ? 'noindex, nofollow' : 'index, follow');
  html = replaceMeta(html, 'property="og:title"', title);
  html = replaceMeta(html, 'property="og:description"', metadata.description);
  html = replaceMeta(html, 'property="og:url"', canonical);
  return html.replace(/(<link rel="canonical" href=")[^"]*(")/, `$1${canonical}$2`);
}
